import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DocumentType } from "constants/adminApi";
import { VerificationStepSummary } from "types/admin";
import { isPdfDocument } from "utils/kycDocument";
import { VerificationOutcomeChip } from "./adminKycVerificationUtils";
import { KycDetailTag, KycDetailTags } from "./kycDocumentDetailTags";
import NameMatchStamp from "./NameMatchStamp";
import { KycDocumentLoadState } from "../hooks/useKycDocumentLoader";

interface AdminKycDocumentPreviewCardProps {
  title: string;
  subtitle?: string;
  documentType: DocumentType;
  objectKey?: string;
  verificationStep?: VerificationStepSummary | null;
  detailTags?: KycDetailTag[];
  /** When set, shows a high-visibility name-match stamp in the card header (bank KYC). */
  nameMatched?: boolean | null;
  /** Opens the name-match explanation dialog (bank KYC). */
  onExplainNameMatch?: () => void;
  /** When true, explain controls are disabled (e.g. missing PAN/bank names). */
  explainNameMatchDisabled?: boolean;
  explainNameMatchDisabledReason?: string;
  state: KycDocumentLoadState;
  onExpand: () => void;
  onDownload: () => void;
  onRetry: () => void;
  downloading?: boolean;
}

function DocumentPreviewContent({
  objectKey,
  state,
  onExpand,
  onRetry,
}: {
  objectKey?: string;
  state: KycDocumentLoadState;
  onExpand: () => void;
  onRetry: () => void;
}) {
  if (!objectKey) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ height: "100%", color: "text.secondary", px: 2, textAlign: "center" }}
      >
        <ImageNotSupportedOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
        <Typography variant="body2">No document uploaded</Typography>
      </Stack>
    );
  }

  if (state.loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
        <CircularProgress size={32} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
          Loading preview...
        </Typography>
      </Stack>
    );
  }

  if (state.error) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={1.5}
        sx={{ height: "100%", px: 2, textAlign: "center" }}
      >
        <Typography variant="body2" color="error.main">
          {state.error}
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={onRetry}>
          Retry
        </Button>
      </Stack>
    );
  }

  if (!state.url) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  const isPdf = isPdfDocument(objectKey);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Expand ${isPdf ? "PDF" : "image"} preview`}
      onClick={onExpand}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onExpand();
        }
      }}
      sx={{
        width: "100%",
        height: "100%",
        cursor: "zoom-in",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      {isPdf ? (
        <Box
          component="iframe"
          src={state.url}
          title="Document preview"
          sx={{
            width: "100%",
            height: "100%",
            border: 0,
            bgcolor: "#fff",
          }}
        />
      ) : (
        <Box
          component="img"
          src={state.url}
          alt="KYC document preview"
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
        />
      )}
    </Box>
  );
}

function AdminKycDocumentPreviewCard({
  title,
  subtitle,
  objectKey,
  verificationStep,
  detailTags = [],
  nameMatched = null,
  onExplainNameMatch,
  explainNameMatchDisabled = false,
  explainNameMatchDisabledReason,
  state,
  onExpand,
  onDownload,
  onRetry,
  downloading = false,
}: AdminKycDocumentPreviewCardProps) {
  const hasDocument = Boolean(objectKey && state.url && !state.loading && !state.error);
  const showNameMatchStamp = nameMatched != null;
  const isNameMismatch = nameMatched === false;
  const showExplainControl = Boolean(onExplainNameMatch);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: isNameMismatch ? "rgba(198, 40, 40, 0.06)" : "transparent",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {showNameMatchStamp && (
            <NameMatchStamp
              matched={Boolean(nameMatched)}
              onClick={onExplainNameMatch}
              disabled={explainNameMatchDisabled}
              disabledReason={explainNameMatchDisabledReason}
            />
          )}
          <VerificationOutcomeChip step={verificationStep} emptyLabel="—" />
        </Stack>
      </Stack>

      <KycDetailTags
        tags={detailTags}
        action={
          showExplainControl ? (
            <Tooltip
              title={
                explainNameMatchDisabled
                  ? explainNameMatchDisabledReason || "Names unavailable for this customer"
                  : "See why the name matched or mismatched"
              }
            >
              <span>
                <Button
                  size="small"
                  onClick={onExplainNameMatch}
                  disabled={explainNameMatchDisabled}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    px: 0.75,
                    minWidth: 0,
                    height: 28,
                  }}
                >
                  Explain match
                </Button>
              </span>
            </Tooltip>
          ) : undefined
        }
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 280,
          bgcolor: "#f0f2f5",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <DocumentPreviewContent
          objectKey={objectKey}
          state={state}
          onExpand={onExpand}
          onRetry={onRetry}
        />
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={0.5}
        sx={{ px: 1, py: 0.75, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Tooltip title="Expand preview">
          <span>
            <IconButton
              size="small"
              aria-label={`Expand ${title}`}
              onClick={onExpand}
              disabled={!hasDocument}
            >
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Download document">
          <span>
            <IconButton
              size="small"
              aria-label={`Download ${title}`}
              onClick={onDownload}
              disabled={!objectKey || downloading}
            >
              {downloading ? (
                <CircularProgress size={18} />
              ) : (
                <DownloadIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
}

export default AdminKycDocumentPreviewCard;
